import { vyDate } from '@vy/utils';

export const DATE_PRESET_KEYS = {
  ThisMonth: 'this-month',
  LastMonth: 'last-month',
  ThisQuarter: 'this-quarter',
  LastQuarter: 'last-quarter',
  Trailing3M: 'trailing-3m',
  Trailing6M: 'trailing-6m',
  Trailing12M: 'trailing-12m',
  YTD: 'ytd',
  Custom: 'custom',
} as const;

export type DatePresetKey = (typeof DATE_PRESET_KEYS)[keyof typeof DATE_PRESET_KEYS];

export const DATE_PRESET_OPTIONS: { value: DatePresetKey; label: string }[] = [
  { value: DATE_PRESET_KEYS.ThisMonth, label: 'This Month' },
  { value: DATE_PRESET_KEYS.LastMonth, label: 'Last Month' },
  { value: DATE_PRESET_KEYS.ThisQuarter, label: 'This Quarter' },
  { value: DATE_PRESET_KEYS.LastQuarter, label: 'Last Quarter' },
  { value: DATE_PRESET_KEYS.Trailing3M, label: 'Trailing 3M' },
  { value: DATE_PRESET_KEYS.Trailing6M, label: 'Trailing 6M' },
  { value: DATE_PRESET_KEYS.Trailing12M, label: 'Trailing 12M' },
  { value: DATE_PRESET_KEYS.YTD, label: 'YTD' },
  { value: DATE_PRESET_KEYS.Custom, label: 'Custom Range' },
];

const KNOWN_PRESETS = new Set<string>(DATE_PRESET_OPTIONS.map((option) => option.value));

export type DatePresetRange = { from: Date; to: Date };

export type ParsedDatePresetValue = {
  preset: DatePresetKey;
  from?: Date;
  to?: Date;
};

export function encodeDatePresetValue(preset: DatePresetKey, from: Date, to: Date): string {
  return `${preset}:${formatIsoDate(from)}:${formatIsoDate(to)}`;
}

export function encodeCustomRange(from: Date, to: Date): string {
  return encodeDatePresetValue(DATE_PRESET_KEYS.Custom, from, to);
}

export function parseDatePresetValue(value: string | undefined | null): ParsedDatePresetValue | null {
  if (!value) {
    return null;
  }

  const [presetRaw, fromIso, toIso] = value.split(':');

  if (!KNOWN_PRESETS.has(presetRaw)) {
    return null;
  }

  const preset = presetRaw as DatePresetKey;
  const from = fromIso ? new Date(fromIso) : undefined;
  const to = toIso ? new Date(toIso) : undefined;

  return { preset, from, to };
}

export function resolveDatePreset(
  value: string | undefined | null,
  now: Date = new Date(),
): DatePresetRange | null {
  const parsed = parseDatePresetValue(value);

  if (!parsed) {
    return null;
  }

  if (parsed.from && parsed.to) {
    return { from: parsed.from, to: parsed.to };
  }

  if (parsed.preset === DATE_PRESET_KEYS.Custom) {
    return null;
  }

  return computePresetRange(parsed.preset, now);
}

export function computePresetRange(preset: DatePresetKey, now: Date = new Date()): DatePresetRange | null {
  const today = vyDate(now);

  switch (preset) {
    case DATE_PRESET_KEYS.ThisMonth:
      return { from: today.startOf('month').toDate(), to: today.endOf('month').toDate() };
    case DATE_PRESET_KEYS.LastMonth: {
      const lastMonth = today.subtract(1, 'month');
      return { from: lastMonth.startOf('month').toDate(), to: lastMonth.endOf('month').toDate() };
    }
    case DATE_PRESET_KEYS.ThisQuarter:
      return resolveQuarterRange(today.year(), getQuarterIndex(today.month()));
    case DATE_PRESET_KEYS.LastQuarter: {
      const currentQuarterIndex = getQuarterIndex(today.month());
      const isFirstQuarter = currentQuarterIndex === 0;
      const lastQuarterYear = isFirstQuarter ? today.year() - 1 : today.year();
      const lastQuarterIndex = isFirstQuarter ? 3 : currentQuarterIndex - 1;
      return resolveQuarterRange(lastQuarterYear, lastQuarterIndex);
    }
    case DATE_PRESET_KEYS.Trailing3M:
      return { from: today.subtract(3, 'month').startOf('day').toDate(), to: today.endOf('day').toDate() };
    case DATE_PRESET_KEYS.Trailing6M:
      return { from: today.subtract(6, 'month').startOf('day').toDate(), to: today.endOf('day').toDate() };
    case DATE_PRESET_KEYS.Trailing12M:
      return { from: today.subtract(12, 'month').startOf('day').toDate(), to: today.endOf('day').toDate() };
    case DATE_PRESET_KEYS.YTD:
      return { from: today.startOf('year').toDate(), to: today.endOf('day').toDate() };
    case DATE_PRESET_KEYS.Custom:
      return null;
    default:
      return null;
  }
}

function getQuarterIndex(monthIndex: number): number {
  return Math.floor(monthIndex / 3);
}

function resolveQuarterRange(year: number, quarterIndex: number): DatePresetRange {
  const firstMonth = quarterIndex * 3;
  const lastMonth = firstMonth + 2;
  const from = vyDate().year(year).month(firstMonth).date(1).startOf('month').toDate();
  const to = vyDate().year(year).month(lastMonth).date(1).endOf('month').toDate();
  return { from, to };
}

function formatIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
